import {
  ActionButton,
  BaseTable,
  Button,
  Card,
  Loading,
  PaginationData,
  PermissionLink,
  TableHeaderSort,
  TableNumber,
  TableSorting,
} from "components";
import moment from "moment";
import { ModalDelete } from "pages/Inventori/Transaction/Received/components";
import { Fragment, useContext, useEffect, useState } from "react";
import { IoSendSharp } from "react-icons/io5";
import { APPROVAL, useToggle } from "utils";
import { FilterSection } from "./components";
import { ReceivedType } from "./types";
import { defaultFilter, useList } from "./utils";
import { DataContext } from "../utils/contexts";
import { PERMISION } from "security";

interface PaginationState {
  total: number;
  page: number;
  limit: number;
  pages: number;
  keyword: string;
  product_group?: string;
  start_date?: string;
  end_date?: string;
  approval_status?: string;
  from_location_id?: string;
  to_location_id?: string;
  received_status?: string;
}

export function ItemsReceived() {
  const modalDelete = useToggle(false);
  const [pagination, setPagination] = useState<PaginationState>(defaultFilter);
  const [deleteData, setDeleteData] = useState<ReceivedType | null>(null);
  const { data, isLoading } = useList(pagination);

  const onClickDelete = (data: ReceivedType) => {
    setDeleteData(data);
    modalDelete.setActive(true);
  };

  const {setFill, setActiveColumn } = useContext(DataContext);

  const header: TableSorting[] = [
    { title: "RECEIVED ID#", value: "trx_code", isActive: true },
    { title: "REF. TRANSFER ID#", value: "transfer_code", isActive: true },
    { title: "DATE", value: "transaction_date",  isActive: true },
    { title: "SOURCE LOCATION", value: "location_from",   isActive: true },
    { title: "TRANSFERED", value: "qty_transfer", isActive: true },
    { title: "DESTINATION LOCATION", value: "location_to", isActive: true },
    { title: "RECEIVED", value: "qty_received", isActive: true },
    { title: "REJECTED", value: "qty_rejected", isActive: true },
    { title: "TRADER", value: "employee_name", isActive: true },
    { title: "APPROVED", value: "approval_status", isActive: true },
    { title: "MEMO", value: "memo", isActive: true },
  ];

  const [tableActive, setTableActive] = useState<TableSorting[]>(header);

  useEffect(() => {
    setActiveColumn && setActiveColumn(tableActive);
    setFill({ 
        dateFrom: pagination.start_date, 
        dateTo: pagination.end_date,
        approval_status: pagination.approval_status,
        received_status: pagination.received_status,
        from_location_id: pagination.from_location_id,
        to_location_id: pagination.to_location_id
    });
  }, [
    setFill,
    pagination.start_date,
    pagination.end_date,
    pagination.approval_status,
    pagination.received_status,
    pagination.from_location_id,
    pagination.to_location_id,
    setActiveColumn,
    tableActive
  ]);


  return (
    <Fragment>
      <section className="my-2">
        {isLoading ? (
          <Loading loading />
        ) : (
          <>
            <section className="mt-2">
              <Card>
                <Card.Body>
                  <FilterSection
                    selected={pagination}
                    setSelected={setPagination}
                    setTableActive={setTableActive}
                    tableActive={tableActive}
                  />
                </Card.Body>
              </Card>
            </section>

            <section className="mt-2">
              <Card>
                <Card.Body>
                  <div className="overflow-auto max-h-[calc(100vh-600px)]">
                    <BaseTable className="border-collapse">
                      <thead className="bg-white sticky top-0 z-[0] !bg-black !text-white">
                        <tr>
                          <th>No</th>

                          <TableHeaderSort
                            title={tableActive}
                          />

                          <th>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.payload.length === 0 ? (
                          <tr>
                            <td colSpan={14} className="text-center">
                              <Loading errorText="No data available" />
                            </td>
                          </tr>
                        ) : (
                          <Fragment>
                            {data?.payload?.map((item: ReceivedType, key: number) => (
                              <tr>
                                <td>
                                  {TableNumber({
                                    pageCurrent: pagination?.page,
                                    dataLength: pagination?.limit!,
                                    index: key,
                                    limit: pagination?.limit,
                                    totalData: pagination?.total,
                                  })}
                                </td>

                                {/* Kolom filter */}
                                {tableActive
                                  .filter((col) => col.isActive !== false)
                                  .map((col, idx) => (
                                    <Fragment key={idx}>
                                      {renderCell(col.value!, item)}
                                    </Fragment>
                                  ))}

                                <td>
                                  <div className="flex justify-center items-center gap-2">
                                    <Button
                                      type="button"
                                      color="ghost"
                                      disabled={[
                                        APPROVAL.REJECTED,
                                        APPROVAL.APPROVED,
                                      ].includes(item?.approval_status)}
                                    >
                                      <PermissionLink
                                        permission={PERMISION.INVENTORY.REGISTER.RECEIVED.UPDATE}
                                        to={`/inventory/transaction/received/edit/${item?.received_id}`}
                                      >
                                        <IoSendSharp />
                                      </PermissionLink>
                                    </Button>
                                    {item?.approval_status !==
                                      APPROVAL.APPROVED && (
                                        <ActionButton.Delete
                                          permission={PERMISION.INVENTORY.REGISTER.RECEIVED.DELETE}
                                          className="join-item"
                                          onClick={() => onClickDelete(item)}
                                        />
                                      )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        )}
                      </tbody>
                    </BaseTable>

                    <div className="mt-5">
                      <PaginationData
                        data={data}
                        pagination={pagination}
                        setPagination={setPagination}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <ModalDelete
                modalState={modalDelete}
                data={{
                  received_id: deleteData?.received_id ?? NaN,
                  transaction_date: deleteData?.transaction_date ?? "",
                  trx_code: deleteData?.trx_code ?? "",
                  location_from_name:
                    deleteData?.location_from.location_name ?? "",
                  location_to_name:
                    deleteData?.location_to?.location_name ?? "",
                  employee_name: deleteData?.employee?.employee_name ?? "",
                  memo: deleteData?.memo ?? "",
                  quantity_received: deleteData?.qty_received ?? 0,
                }}
                onSuccess={() => {
                  modalDelete.setActive(false);
                  setDeleteData(null);
                }}
              />
            </section>
          </>
        )}
      </section>
    </Fragment>
  );
}

function renderCell(value: string, item: ReceivedType) {
  switch (value) {
    case "trx_code":
      return <td>{item?.trx_code}</td>
    case "transfer_code":
      return <td>{item?.transfer?.trx_code}</td>
    case "transaction_date":
      return <td>{moment(item?.transaction_date).format("DD/MM/YYYY")}</td>
    case "location_from":
      return <td>{item?.location_from?.location_name}</td>
    case "qty_transfer":
      return <td className="text-center">{item?.qty_transfer}</td>
    case "location_to":
      return <td>{item?.location_to?.location_name}</td>
    case "qty_received":
      return <td className="text-center">{item?.qty_received}</td>
    case "qty_rejected":
      return <td className="text-center">{item?.qty_rejected}</td>
    case "employee_name":
      return <td>{item?.employee?.employee_name}</td>
    case "approval_status":
      return <td>{APPROVAL[item?.approval_status]}</td>
    case "memo":
      return <td>{item?.memo}</td>
    default:
      return <></>;
  }
}
